import React, { useEffect, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Save as SaveIcon,
    Close as CloseIcon,
    ReceiptLong as ReceiptLongIcon,
    CalendarMonth as CalendarMonthIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useValuations } from '../../../hooks/administration/useValuations';

const schema = yup.object({
    proid: yup
        .number()
        .typeError('Debe seleccionar un proyecto.')
        .required('El proyecto es obligatorio.'),
    monid: yup
        .number()
        .typeError('Debe seleccionar una moneda.')
        .required('La moneda es obligatoria.'),
    tipimpid: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === '' || originalValue === null || originalValue === undefined
                ? null
                : value
        ),
    valcabfecini: yup
        .string()
        .required('La fecha de inicio es obligatoria.'),
    valcabfecfin: yup
        .string()
        .required('La fecha de fin es obligatoria.')
        .test(
            'is-after-start',
            'La fecha fin no puede ser menor que la fecha inicio.',
            function (value) {
                const { valcabfecini } = this.parent;
                if (!valcabfecini || !value) return true;
                return value >= valcabfecini;
            }
        ),
    valcabdes: yup
        .string()
        .nullable()
        .max(1000, 'La descripción no debe superar los 1000 caracteres.'),
    valcabest: yup
        .string()
        .oneOf(['CREADA', 'EN_PROCESO'], 'El estado inicial debe ser CREADA o EN_PROCESO.')
        .required('El estado inicial es obligatorio.'),
});

const EMPTY_FORM = {
    proid: '',
    monid: '',
    tipimpid: '',
    valcabfecini: '',
    valcabfecfin: '',
    valcabdes: '',
    valcabest: 'CREADA',
};

const INFO_STATE = {
    CREADA: {
        label: 'CREADA',
        color: '#90a4ae',
        text: '#fff',
        description: 'La valorización queda creada y lista para que el usuario agregue órdenes, ajustes y realice revisiones iniciales.',
    },
    EN_PROCESO: {
        label: 'EN PROCESO',
        color: '#1976d2',
        text: '#fff',
        description: 'La valorización se crea como documento que ya está siendo trabajado activamente por el equipo responsable.',
    },
};

const ValuationForm = ({
    open,
    handleClose,
    defaultValues = {},
    projects = [],
    currencies = [],
    taxes = [],
}) => {
    const isEditMode = Boolean(defaultValues?.valcabid);

    const {
        createManualMutation,
        updateHeaderMutation,
    } = useValuations();

    const safeDefaults = useMemo(() => {
        if (!defaultValues || Object.keys(defaultValues).length === 0) {
            return EMPTY_FORM;
        }

        return {
            proid: defaultValues.proid ?? '',
            monid: defaultValues.monid ?? '',
            tipimpid: defaultValues.tipimpid ?? '',
            valcabfecini: defaultValues.valcabfecini ?? '',
            valcabfecfin: defaultValues.valcabfecfin ?? '',
            valcabdes: defaultValues.valcabdes ?? '',
            valcabest: ['CREADA', 'EN_PROCESO'].includes(defaultValues.valcabest)
                ? defaultValues.valcabest
                : 'CREADA',
        };
    }, [defaultValues]);

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isDirty },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: safeDefaults,
    });

    useEffect(() => {
        if (open) {
            reset(safeDefaults);
        }
    }, [open, safeDefaults, reset]);

    const watchedState = watch('valcabest');
    const watchedProject = watch('proid');
    const watchedCurrency = watch('monid');
    const watchedTax = watch('tipimpid');
    const watchedStart = watch('valcabfecini');
    const watchedEnd = watch('valcabfecfin');

    const selectedProject = projects.find((p) => Number(p.proid) === Number(watchedProject));
    const selectedCurrency = currencies.find((m) => Number(m.monid) === Number(watchedCurrency));
    const selectedTax = taxes.find((t) => Number(t.tipimpid) === Number(watchedTax));

    const stateInfo = INFO_STATE[watchedState] || INFO_STATE.CREADA;

    const isSubmitting =
        createManualMutation.isPending || updateHeaderMutation.isPending;

    const onSubmit = (formData) => {
        const payload = {
            proid: Number(formData.proid),
            monid: Number(formData.monid),
            tipimpid:
                formData.tipimpid === '' || formData.tipimpid === null
                    ? null
                    : Number(formData.tipimpid),
            valcabfecini: formData.valcabfecini,
            valcabfecfin: formData.valcabfecfin,
            valcabdes: formData.valcabdes?.trim() || null,
            valcabest: formData.valcabest,
        };

        if (isEditMode) {
            updateHeaderMutation.mutate(
                {
                    id: defaultValues.valcabid,
                    data: payload,
                },
                {
                    onSuccess: () => {
                        handleClose();
                    },
                }
            );
            return;
        }

        createManualMutation.mutate(payload, {
            onSuccess: () => {
                handleClose();
            },
        });
    };

    const title = isEditMode ? 'Editar valorización' : 'Nueva valorización manual';
    const submitLabel = isEditMode ? 'Guardar cambios' : 'Crear valorización';

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ pb: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1.2}>
                    <ReceiptLongIcon color="primary" />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Define la cabecera del documento antes de asociar órdenes de servicio.
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
                <Box component="form" id="valuation-form" onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2.5}>
                        {/* Mensaje principal */}
                        <Alert severity="info" variant="outlined">
                            <Typography variant="body2">
                                Este formulario crea o actualiza únicamente la <strong>cabecera</strong> de la valorización.
                                Las órdenes de servicio y los ajustes se gestionan después desde el detalle.
                            </Typography>
                        </Alert>

                        {/* Bloque 1: Contexto general */}
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <AccountBalanceWalletIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Contexto del documento
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="proid"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                fullWidth
                                                size="small"
                                                label="Proyecto"
                                                error={!!errors.proid}
                                                helperText={
                                                    errors.proid?.message ||
                                                    'Selecciona el proyecto al que pertenece esta valorización.'
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccionar proyecto</em>
                                                </MenuItem>
                                                {projects.map((project) => (
                                                    <MenuItem key={project.proid} value={project.proid}>
                                                        {project.pronom}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Controller
                                        name="monid"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                fullWidth
                                                size="small"
                                                label="Moneda"
                                                error={!!errors.monid}
                                                helperText={
                                                    errors.monid?.message ||
                                                    'Moneda en la que se cobrará esta valorización.'
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccionar moneda</em>
                                                </MenuItem>
                                                {currencies.map((currency) => (
                                                    <MenuItem key={currency.monid} value={currency.monid}>
                                                        {currency.moncod} - {currency.monnom}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Controller
                                        name="tipimpid"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                fullWidth
                                                size="small"
                                                label="Impuesto"
                                                error={!!errors.tipimpid}
                                                helperText={
                                                    errors.tipimpid?.message ||
                                                    'Opcional. Se usará para calcular el impuesto total.'
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>Sin impuesto</em>
                                                </MenuItem>
                                                {taxes.map((tax) => (
                                                    <MenuItem key={tax.tipimpid} value={tax.tipimpid}>
                                                        {tax.tipimpnom || tax.tipimpdes || `Impuesto ${tax.tipimpid}`}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                            </Grid>

                            {(selectedProject || selectedCurrency || selectedTax) && (
                                <Box mt={2}>
                                    <Divider sx={{ mb: 1.5 }} />
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {selectedProject && (
                                            <Chip
                                                label={`Proyecto: ${selectedProject.pronom}`}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {selectedCurrency && (
                                            <Chip
                                                label={`Moneda: ${selectedCurrency.moncod}`}
                                                color="success"
                                                variant="outlined"
                                            />
                                        )}
                                        {selectedTax && (
                                            <Chip
                                                label={`Impuesto: ${selectedTax.tipimpnom || selectedTax.tipimpdes || selectedTax.tipimpid}`}
                                                color="warning"
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            )}
                        </Paper>

                        {/* Bloque 2: Periodo */}
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <CalendarMonthIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Periodo de valorización
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="valcabfecini"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="date"
                                                fullWidth
                                                size="small"
                                                label="Fecha inicio"
                                                InputLabelProps={{ shrink: true }}
                                                error={!!errors.valcabfecini}
                                                helperText={
                                                    errors.valcabfecini?.message ||
                                                    'Desde qué fecha se considerarán servicios dentro del periodo.'
                                                }
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="valcabfecfin"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="date"
                                                fullWidth
                                                size="small"
                                                label="Fecha fin"
                                                InputLabelProps={{ shrink: true }}
                                                error={!!errors.valcabfecfin}
                                                helperText={
                                                    errors.valcabfecfin?.message ||
                                                    'Hasta qué fecha se considerarán servicios dentro del periodo.'
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>

                            {(watchedStart || watchedEnd) && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        Solo podrán asociarse órdenes de servicio elegibles dentro del rango{' '}
                                        <strong>{watchedStart || '---'}</strong> a <strong>{watchedEnd || '---'}</strong>.
                                    </Typography>
                                </Alert>
                            )}
                        </Paper>

                        {/* Bloque 3: Estado inicial */}
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Estado inicial
                            </Typography>

                            <Controller
                                name="valcabest"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        label="Estado inicial"
                                        error={!!errors.valcabest}
                                        helperText={
                                            errors.valcabest?.message ||
                                            'Solo se permite iniciar en CREADA o EN_PROCESO.'
                                        }
                                    >
                                        <MenuItem value="CREADA">CREADA</MenuItem>
                                        <MenuItem value="EN_PROCESO">EN_PROCESO</MenuItem>
                                    </TextField>
                                )}
                            />

                            <Box mt={2}>
                                <Chip
                                    label={stateInfo.label}
                                    sx={{
                                        backgroundColor: stateInfo.color,
                                        color: stateInfo.text,
                                        fontWeight: 'bold',
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" mt={1.2}>
                                    {stateInfo.description}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Bloque 4: Descripción */}
                        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Observación o descripción
                            </Typography>

                            <Controller
                                name="valcabdes"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        label="Descripción"
                                        placeholder="Ejemplo: Valorización correspondiente a servicios de transporte del periodo mensual del proyecto..."
                                        error={!!errors.valcabdes}
                                        helperText={
                                            errors.valcabdes?.message ||
                                            'Este texto ayuda a identificar el documento y dar contexto al cliente o al equipo interno.'
                                        }
                                    />
                                )}
                            />
                        </Paper>

                        {/* Bloque 5: Resumen visual */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                backgroundColor: '#fff',
                                borderStyle: 'dashed',
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                Resumen de lo que estás configurando
                            </Typography>

                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Proyecto
                                    </Typography>
                                    <Typography fontWeight="medium">
                                        {selectedProject?.pronom || 'No seleccionado'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Moneda
                                    </Typography>
                                    <Typography fontWeight="medium">
                                        {selectedCurrency
                                            ? `${selectedCurrency.moncod} - ${selectedCurrency.monnom}`
                                            : 'No seleccionada'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Impuesto
                                    </Typography>
                                    <Typography fontWeight="medium">
                                        {selectedTax?.tipimpnom || selectedTax?.tipimpdes || 'Sin impuesto'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Periodo
                                    </Typography>
                                    <Typography fontWeight="medium">
                                        {watchedStart || '---'} a {watchedEnd || '---'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Estado inicial
                                    </Typography>
                                    <Typography fontWeight="medium">
                                        {stateInfo.label}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Error global de mutation */}
                        {(createManualMutation.isError || updateHeaderMutation.isError) && (
                            <Alert severity="error">
                                {createManualMutation.error?.message ||
                                    updateHeaderMutation.error?.message ||
                                    'Ocurrió un error al guardar la valorización.'}
                            </Alert>
                        )}
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    startIcon={<CloseIcon />}
                >
                    Cancelar
                </Button>

                <Button
                    type="submit"
                    form="valuation-form"
                    variant="contained"
                    disabled={isSubmitting || (isEditMode && !isDirty)}
                    startIcon={<SaveIcon />}
                >
                    {isSubmitting ? 'Guardando...' : submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ValuationForm;